import { ArrowLeft, Check, Eye, EyeOff, RefreshCw, RotateCcw, X } from 'lucide-react';
import { useState } from 'react';
import type { AssessmentDetail, Dimension } from '@/lib/assessment';
import { DIMENSION_META } from '@/lib/assessment';
import { Button } from '@/components/ui/button';
import { BackToListButton } from '@/components/BackToListButton';
import { cn } from '@/lib/utils';

type Props = {
  result: AssessmentDetail;
  onRetake: () => void;
  onBack: () => void;
  /** Jump straight to the Dashboard jobs list (unmounts this view). */
  onHome: () => void;
};

function scoreTone(score: number): {
  ring: string;
  text: string;
  bg: string;
  bar: string;
} {
  if (score >= 80) return { ring: 'ring-emerald-500/40', text: 'text-emerald-600', bg: 'bg-emerald-500/10', bar: 'bg-emerald-500' };
  if (score >= 60) return { ring: 'ring-sky-500/40', text: 'text-sky-600', bg: 'bg-sky-500/10', bar: 'bg-sky-500' };
  if (score >= 40) return { ring: 'ring-amber-500/40', text: 'text-amber-600', bg: 'bg-amber-500/10', bar: 'bg-amber-500' };
  return { ring: 'ring-rose-500/40', text: 'text-rose-600', bg: 'bg-rose-500/10', bar: 'bg-rose-500' };
}

function ScoreCard({
  dimension,
  score,
  level,
}: {
  dimension: keyof typeof DIMENSION_META;
  score: number;
  level: string;
}) {
  const meta = DIMENSION_META[dimension];
  const Icon = meta.icon;
  const tone = scoreTone(score);
  return (
    <div className={cn('rounded-xl border border-border bg-card p-4 ring-1', tone.ring)}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
            <Icon className="h-4 w-4 text-primary" strokeWidth={2} />
            <span className="truncate">{meta.label}</span>
          </p>
          <p className={cn('mt-0.5 text-xs font-medium', tone.text)}>{level}</p>
        </div>
        <div className="text-right">
          <span className={cn('text-2xl font-bold tabular-nums', tone.text)}>{score}</span>
          <span className="text-sm text-muted-foreground">/100</span>
        </div>
      </div>
      <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-muted">
        <div className={cn('h-full rounded-full', tone.bar)} style={{ width: `${score}%` }} />
      </div>
    </div>
  );
}

function ReviewRow({
  item,
  showAnswers,
}: {
  item: AssessmentDetail['review'][number];
  showAnswers: boolean;
}) {
  const meta = DIMENSION_META[item.dimension];
  const DimIcon = meta.icon;
  const correctSet = new Set(Array.isArray(item.answer) ? item.answer : item.answer ? [item.answer] : []);
  const selectedSet = new Set(Array.isArray(item.selected) ? item.selected : item.selected ? [item.selected] : []);
  const isCorrect = item.options.every((o) => correctSet.has(o) === selectedSet.has(o)) && correctSet.size === selectedSet.size;

  return (
    <div className="rounded-xl border border-border bg-card p-3.5">
      <div className="flex items-start gap-2">
        {showAnswers ? (
          <span
            className={cn(
              'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full',
              isCorrect ? 'bg-emerald-500/15 text-emerald-600' : 'bg-rose-500/15 text-rose-600',
            )}
          >
            {isCorrect ? <Check className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5" />}
          </span>
        ) : (
          <span
            className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground"
            aria-label="Answer hidden"
          >
            <EyeOff className="h-3 w-3" />
          </span>
        )}
        <div className="min-w-0 flex-1">
          <p className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            <DimIcon className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={2} />
            <span>{meta.short}</span>
          </p>
          <p className="mt-1 text-sm font-medium leading-snug text-foreground">{item.stem}</p>
          <ul className="mt-2 space-y-1">
            {item.options.map((opt) => {
              const isAns = correctSet.has(opt);
              const isPicked = selectedSet.has(opt);
              return (
                <li
                  key={opt}
                  className={cn(
                    'flex items-start gap-1.5 rounded-md px-2 py-1 text-xs',
                    showAnswers
                      ? isAns
                        ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400'
                        : isPicked
                          ? 'bg-rose-500/10 text-rose-700 dark:text-rose-400'
                          : 'text-muted-foreground'
                      : isPicked
                        ? 'bg-primary/10 text-foreground ring-1 ring-primary/30'
                        : 'text-muted-foreground',
                  )}
                >
                  <span className="mt-px shrink-0">
                    {showAnswers ? (
                      isAns ? (
                        <Check className="h-3.5 w-3.5" />
                      ) : isPicked ? (
                        <X className="h-3.5 w-3.5" />
                      ) : (
                        '•'
                      )
                    ) : isPicked ? (
                      <Check className="h-3.5 w-3.5 text-primary" />
                    ) : (
                      '•'
                    )}
                  </span>
                  <span className="leading-snug">
                    {opt}
                    {showAnswers ? (
                      isAns ? (
                        <span className="ml-1 font-medium">
                          ({item.dimension === 'willingness' ? 'expected' : 'correct'})
                        </span>
                      ) : isPicked ? (
                        <span className="ml-1 font-medium">(your pick)</span>
                      ) : null
                    ) : isPicked ? (
                      <span className="ml-1 text-muted-foreground">(your pick)</span>
                    ) : null}
                  </span>
                </li>
              );
            })}
          </ul>
          {!showAnswers && item.selected === null ? (
            <p className="mt-1.5 text-[11px] italic text-muted-foreground">(skipped)</p>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export function AssessmentResults({ result, onRetake, onBack, onHome }: Props) {
  const review = result.review;
  // Answers are hidden by default to discourage immediate checking, which
  // would diminish the value of retaking the assessment after preparation.
  const [showAnswers, setShowAnswers] = useState(false);

  const counts = review.reduce(
    (acc, item) => {
      const correctSet = new Set(Array.isArray(item.answer) ? item.answer : item.answer ? [item.answer] : []);
      const selectedSet = new Set(
        Array.isArray(item.selected) ? item.selected : item.selected ? [item.selected] : [],
      );
      const isCorrect =
        item.options.every((o) => correctSet.has(o) === selectedSet.has(o)) &&
        correctSet.size === selectedSet.size;
      acc[isCorrect ? 'right' : 'wrong'] += 1;
      return acc;
    },
    { right: 0, wrong: 0 },
  );

  return (
    <div className="flex min-h-dvh flex-col bg-background">
      <div className="sticky top-0 z-10 flex items-center justify-between gap-2 border-b border-border bg-card/95 px-2 py-2 backdrop-blur">
        <Button variant="ghost" size="sm" onClick={onBack} className="gap-1">
          <ArrowLeft className="h-4 w-4" /> Back
        </Button>
        {/* onClick wraps onRetake so the click MouseEvent isn't passed as the
            (optional) quizOverride arg into startTaking — that would make
            takingQuiz the event object and crash allQuestions() on the next
            render. */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onRetake()}
          className="gap-1 text-muted-foreground"
        >
          <RotateCcw className="h-4 w-4" /> Retake
        </Button>
      </div>

      <div className="mx-auto w-full max-w-2xl px-4 pt-4 pb-24 sm:px-6">
        <div className="mb-1 flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
          <RefreshCw className="h-3.5 w-3.5" /> Self-assessment result
        </div>
        <h1 className="text-lg font-semibold leading-snug tracking-tight">
          {result.willingnessScore.score + result.hardScore.score + result.softScore.score > 0
            ? 'Your match snapshot'
            : 'Assessment complete'}
        </h1>
        <p className="mt-1 text-xs text-muted-foreground">
          {counts.right} / {review.length} correct ·{' '}
          <span className="text-emerald-600">{counts.right} right</span> ·{' '}
          <span className="text-rose-600">{counts.wrong} to review</span>
        </p>

        {/* Score cards — one per assessed dimension. A single-dimension
            practice attempt renders just its dimension's card instead of three
            (two of which would be misleading zeros). */}
        <div className="mt-4 space-y-3">
          {(result.dimensions ?? ([
            'willingness',
            'hard_requirements',
            'soft_requirements',
          ] as Dimension[])).map((dim) => (
            <ScoreCard
              key={dim}
              dimension={dim}
              score={
                dim === 'willingness'
                  ? result.willingnessScore.score
                  : dim === 'hard_requirements'
                    ? result.hardScore.score
                    : result.softScore.score
              }
              level={
                dim === 'willingness'
                  ? result.willingnessScore.level
                  : dim === 'hard_requirements'
                    ? result.hardScore.level
                    : result.softScore.level
              }
            />
          ))}
        </div>

        <div className="mt-6 mb-2 flex items-center justify-between gap-2">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Review
          </h2>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowAnswers((s) => !s)}
            className="gap-1 text-muted-foreground"
            aria-pressed={showAnswers}
          >
            {showAnswers ? (
              <>
                <EyeOff className="h-3.5 w-3.5" /> Hide answers
              </>
            ) : (
              <>
                <Eye className="h-3.5 w-3.5" /> Reveal answers
              </>
            )}
          </Button>
        </div>
        {!showAnswers ? (
          <p className="mb-2 text-[11px] text-muted-foreground">
            Correct answers and right/wrong indicators are hidden. Try retaking the
            quiz after some preparation to see if you improved.
          </p>
        ) : null}
        <div className="space-y-2.5">
          {review.map((item, i) => (
            <ReviewRow key={`${item.dimension}-${i}`} item={item} showAnswers={showAnswers} />
          ))}
        </div>

        <div className="mt-6 flex flex-col gap-2">
          <Button onClick={() => onRetake()} className="gap-1.5">
            <RotateCcw className="h-4 w-4" /> Retake assessment
          </Button>
          <Button variant="outline" onClick={onBack} className="gap-1">
            <ArrowLeft className="h-4 w-4" /> Back to job
          </Button>
        </div>
      </div>
      <BackToListButton onHome={onHome} />
    </div>
  );
}
